import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from './Home.module.css';

const stats = [
  { value: '2+', suffix: ' Years', label: 'Industry Experience' },
  { value: '20+', suffix: '', label: 'Projects Delivered' },
];

const trustedBy = [
  { name: 'Pure Prakrati', img: '/images/logos/pp.png' },
  { name: 'USTL', img: '/images/logos/ustl.png' },
  { name: 'Devta Gensets', img: '/images/logos/dg.png' },
  { name: 'KGVL', img: '/images/logos/kgvl.png' },
  { name: 'TWI', img: '/images/logos/twi.png' },
  { name: 'GFGM', img: '/images/logos/gfgm.png' },
  { name: 'winsol', img: '/images/logos/winsol.jpg' },
];

const services = [
  {
    title: 'Employ End-To-End Custom Web Development and Design Services',
    body: 'Our craftsmen develop digital-first experiences and solutions that grow brands, build audiences and deliver impact at scale.',
    cta: 'Explore more',
    to: '/projects',
    visual: '/images/others/r.png',
    tag: 'Web',
  },
  {
    title: 'Mobile App Development',
    body: 'From concept to App Store, we build cross-platform mobile experiences that users love and businesses rely on.',
    cta: 'Explore more',
    to: '/projects',
    visual: '/images/others/mobile.png',
    tag: 'Mobile',
  },
];

const footerBlurbs = [
  { heading: 'Customer-centricity', body: "We prioritize our client's needs, actively listening to their concerns and delivering solutions that align with their goals" },
  { heading: 'Innovation', body: 'We foster a culture of creativity and continuous improvement, embracing new ideas and technologies to provide cutting-edge solutions.' },
  { heading: 'Adaptability', body: "In an ever-changing technological landscape, we remain flexible and open to change, enabling us to pivot and respond effectively to our client's evolving needs." },
];

function ParticleCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles, raf;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      initParticles();
    };

    const initParticles = () => {
      const count = Math.min(Math.floor((W * H) / 10000), 120);
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.8 + 0.4,
        alpha: Math.random() * 0.5 + 0.1,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        const dx = p.x - mouse.x, dy = p.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          const force = (120 - dist) / 120;
          p.vx += (dx / dist) * force * 0.6;
          p.vy += (dy / dist) * force * 0.6;
        }
        p.vx *= 0.97; p.vy *= 0.97;
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,32,32,${p.alpha})`;
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(224,32,32,${0.12 * (1 - d / 110)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };

    const onMove = e => { mouse.x = e.clientX; mouse.y = e.clientY - canvas.getBoundingClientRect().top; };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    resize();
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={canvasRef} className={styles.particleCanvas} aria-hidden="true" />;
}


function SplitWords({ text, className, charClass }) {
  return (
    <span className={className} aria-label={text}>
      {text.split(' ').map((word, wi) => (
        <span key={wi} className={styles.wordWrap}>
          {word.split('').map((ch, ci) => (
            <span
              key={ci}
              className={charClass || styles.char}
              style={{ '--wi': wi, '--ci': ci, '--total': word.length }}
            >{ch}</span>
          ))}
          {' '}
        </span>
      ))}
    </span>
  );
}

function Magnetic({ children, strength = 0.4, className = '' }) {
  const ref = useRef(null);
  const cur = useRef({ x: 0, y: 0 });
  const raf = useRef(null);
  const onMove = e => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    cur.current = { x: (e.clientX - r.left - r.width / 2) * strength, y: (e.clientY - r.top - r.height / 2) * strength };
  };
  const onLeave = () => { cur.current = { x: 0, y: 0 }; };
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let lx = 0, ly = 0;
    const tick = () => {
      lx += (cur.current.x - lx) * 0.14;
      ly += (cur.current.y - ly) * 0.14;
      el.style.transform = `translate(${lx}px,${ly}px)`;
      raf.current = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf.current);
  }, []);
  return (
    <div ref={ref} className={`${styles.magneticWrap} ${className}`}
      onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  );
}

function LiquidBtn({ to, onClick, children, variant = 'red', size = 'md' }) {
  const blobRef = useRef(null);
  const onMove = e => {
    const r = e.currentTarget.getBoundingClientRect();
    blobRef.current?.style.setProperty('--lx', `${((e.clientX - r.left) / r.width) * 100}%`);
    blobRef.current?.style.setProperty('--ly', `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const cls = [styles.liquidBtn, styles[`lBtn--${variant}`], styles[`lBtn--${size}`]].join(' ');
  const inner = <>
    <span className={styles.lBlobWrap}><span className={styles.lBlob} ref={blobRef} /></span>
    <span className={styles.lBtnLabel}>{children}</span>
    <span className={styles.lBtnArrow} aria-hidden="true">↗</span>
  </>;
  return to
    ? <Link to={to} className={cls} onMouseMove={onMove} data-cursor>{inner}</Link>
    : <button onClick={onClick} className={cls} onMouseMove={onMove} data-cursor>{inner}</button>;
}

function Ticker({ items, speed = 40, reverse = false }) {
  const text = items.join('  ·  ') + '  ·  ';
  return (
    <div className={styles.ticker} aria-hidden="true">
      <div className={`${styles.tickerTrack} ${reverse ? styles.tickerReverse : ''}`}
        style={{ '--ticker-speed': `${speed}s` }}>
        {[...Array(4)].map((_, i) => <span key={i}>{text}</span>)}
      </div>
    </div>
  );
}

function Counter({ target, suffix = '' }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      const end = parseInt(target, 10);
      let start = 0, startTime = null;
      const step = ts => {
        if (!startTime) startTime = ts;
        const prog = Math.min((ts - startTime) / 1400, 1);
        const ease = 1 - Math.pow(1 - prog, 4);
        setVal(Math.floor(ease * end));
        if (prog < 1) requestAnimationFrame(step);
        else setVal(end);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function useReveal(selector, options = {}) {
  useEffect(() => {
    const { delay = 0, threshold = 0.15, cls = styles.isVisible } = options;
    const els = document.querySelectorAll(selector);
    const obs = new IntersectionObserver(entries => {
      entries.forEach((entry, i) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        setTimeout(() => el.classList.add(cls), delay + i * 80);
        obs.unobserve(el);
      });
    }, { threshold });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [selector]);
}

function useGSAPEffects() {
  useEffect(() => {
    let lenis, gsap, ST, ctx;
    (async () => {
      try {
        const gm = await import('gsap');
        const sm = await import('gsap/ScrollTrigger');
        gsap = gm.gsap || gm.default;
        ST = sm.ScrollTrigger;
        gsap.registerPlugin(ST);
      } catch { return; }

      // Disabled Lenis smooth-scrolling to prevent "lazy/lagged" scrolling on the homepage,
      // matching the native scrolling behavior of the other pages on the website.
      /*
      try {
        const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!isTouch) {
          const LM    = await import('@studio-freight/lenis');
          const Lenis = LM.default || LM.Lenis;
          lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
          const raf = t => { lenis.raf(t); requestAnimationFrame(raf); };
          requestAnimationFrame(raf);
          lenis.on('scroll', ST.update);
          gsap.ticker.lagSmoothing(0);
        }
      } catch {}
      */

      ctx = gsap.context(() => {
        gsap.fromTo(`.${styles.heroTitle} .${styles.char}`,
          { yPercent: 110, opacity: 0, rotateX: -60, skewY: 4 },
          {
            yPercent: 0, opacity: 1, rotateX: 0, skewY: 0,
            stagger: { each: 0.022, from: 'start' },
            duration: 1.1, ease: 'expo.out', delay: 0.3,
            transformOrigin: '0% 50% -20px'
          });

        gsap.fromTo(`.${styles.heroEyebrow}`,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out', delay: 0.15 });

        gsap.fromTo(`.${styles.heroCtas} .${styles.magneticWrap}`,
          { y: 30, opacity: 0, scale: 0.92 },
          {
            y: 0, opacity: 1, scale: 1,
            stagger: 0.12, duration: 0.9, ease: 'back.out(1.8)', delay: 1.0
          });

        gsap.fromTo(`.${styles.stat}`,
          { y: 40, opacity: 0, scale: 0.85 },
          {
            y: 0, opacity: 1, scale: 1,
            stagger: 0.18, duration: 0.8, ease: 'back.out(2)', delay: 1.3
          });

        document.querySelectorAll(`.${styles.svcPanel}`).forEach((panel, i) => {
          const img = panel.querySelector(`.${styles.svcVisualWrap}`);
          const text = panel.querySelector(`.${styles.svcText}`);
          const tag = panel.querySelector(`.${styles.svcTag}`);
          const chars = panel.querySelectorAll(`.${styles.svcH2} .${styles.char}`);
          const body = panel.querySelector(`.${styles.svcBody}`);
          const btn = panel.querySelector(`.${styles.magneticWrap}`);
          const tiles = panel.querySelectorAll(`.${styles.imgTile}`);
          const glow = panel.querySelector(`.${styles.imgGlow}`);

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: panel,
              start: 'top 80%',
              toggleActions: 'play none none none',
            }
          });

          tl.fromTo(tag, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(3)' }, 0);

          tl.fromTo(tiles,
            { scale: 0.7, opacity: 0, rotate: () => (Math.random() - 0.5) * 12 },
            {
              scale: 1, opacity: 1, rotate: 0,
              stagger: 0.07, duration: 0.9, ease: 'expo.out'
            }, 0.1);

          tl.fromTo(glow, { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 1.2, ease: 'power2.out' }, 0.3);

          tl.fromTo(chars,
            { yPercent: 110, opacity: 0 },
            {
              yPercent: 0, opacity: 1,
              stagger: 0.016, duration: 0.8, ease: 'power4.out'
            }, 0.35);

          tl.fromTo(body, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.7);
          tl.fromTo(btn, { y: 20, opacity: 0, scale: 0.9 }, { y: 0, opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(2)' }, 0.85);

          gsap.to(img, {
            yPercent: -12,
            ease: 'none',
            scrollTrigger: { trigger: panel, start: 'top bottom', end: 'bottom top', scrub: 1.2 },
          });
        });

        gsap.fromTo(`.${styles.blurbCard}`,
          { y: 70, opacity: 0, scale: 0.9 },
          {
            y: 0, opacity: 1, scale: 1,
            stagger: 0.13, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: `.${styles.blurbs}`, start: 'top 80%' }
          });

        gsap.fromTo(`.${styles.marqueeWrapper}`,
          { opacity: 0 }, {
          opacity: 1, duration: 0.8,
          scrollTrigger: { trigger: `.${styles.marqueeWrapper}`, start: 'top 90%' }
        });

      });
    })();

    return () => {
      ctx?.revert();
      lenis?.destroy();
    };
  }, []);
}

export default function Home() {
  useGSAPEffects();
  useReveal(`[data-reveal]`);
  useReveal(`[data-reveal-card]`, { threshold: 0.1 });

  const [showBanner, setShowBanner] = useState(true);

  const meshRef = useRef(null);
  const onHeroMove = useCallback(e => {
    if (!meshRef.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    meshRef.current.style.background =
      `radial-gradient(ellipse 60% 55% at ${x}% ${y}%, rgba(224,32,32,0.13) 0%, transparent 70%)`;
  }, []);

  return (
    <div className={styles.page}>

      {showBanner && (
        <div className="promo-overlay" onClick={() => setShowBanner(false)}>
          <div className="promo-card" onClick={(e) => e.stopPropagation()}>

            {/* ─── LEFT COLUMN ─── */}
            <div className="promo-left">
              <span className="promo-badge">
                <span className="promo-badge-icon">✦</span>
                GFGM S3E2 MISSION
              </span>

              <h2 className="promo-heading">
                One Student —<br />
                <span className="promo-accent">One Startup.</span>
              </h2>

              <p className="promo-keywords">
                STUDENT&nbsp;&nbsp;•&nbsp;&nbsp;SKILL&nbsp;&nbsp;•&nbsp;&nbsp;STARTUP&nbsp;&nbsp;•&nbsp;&nbsp;ENVIRONMENT&nbsp;&nbsp;•&nbsp;&nbsp;ENTREPRENEURSHIP&nbsp;
              </p>

              <p className="promo-desc">
                Empowering ambitious businesses with real-world digital
                solutions, rapid delivery from Day 1, and industry-leading
                results.
              </p>

              <div className="promo-note">
                <span className="promo-check">✓</span>
                Trusted by 20+ businesses worldwide
              </div>

              <div className="promo-divider" />

              <p className="promo-footer-text">
                Download our app to get started today!
              </p>
            </div>

            {/* ─── RIGHT COLUMN ─── */}
            <div className="promo-right">
              <button
                className="promo-close"
                onClick={() => setShowBanner(false)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="promo-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="2" width="12" height="20" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10 19H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>

              <h3 className="promo-app-title">Join Our App</h3>
              <p className="promo-app-sub">Official Application</p>

              <a
                href="https://play.google.com/store/apps/details?id=com.tsilteam.KGVHybridSol&hl=en-IN"
                target="_blank"
                rel="noopener noreferrer"
                className="promo-store-btn promo-store-btn--primary"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1.5 1L11 8L1.5 15V1Z" fill="currentColor" /></svg>
                <span><em>Join us on</em>Google Play Store</span>
              </a>

              <a href="https://apps.apple.com/in/app/kgv-hybrid/id6810613659" className="promo-store-btn promo-store-btn--disabled" onClick={(e) => e.preventDefault()}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.4 1.3c-.6.7-1.5 1.2-2.4 1.1-.1-.9.3-1.8.9-2.4.6-.7 1.6-1.2 2.4-1.2.1 1-.3 1.9-.9 2.5zM13.6 11.2c-.3.7-.5 1-1 1.6-.6.8-1.4 1.9-2.4 1.9-.9 0-1.1-.6-2.3-.6s-1.5.6-2.3.6c-1 0-1.7-1-2.4-1.8C1.7 11 1 8.8 1.9 7.2c.6-1 1.6-1.6 2.6-1.6.9 0 1.5.6 2.3.6.7 0 1.2-.6 2.3-.6.8 0 1.7.4 2.3 1.2-2 1.1-1.7 4 .2 4.4z" fill="currentColor" /></svg>
                <span><em>Join us on</em>Apple App Store</span>

              </a>
            </div>

          </div>
        </div>
      )}

      <style>{`
        .promo-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(8px);
          padding: 1.5rem;
        }

        .promo-card {
          position: relative;
          display: flex;
          max-width: 800px;
          width: 100%;
          background: rgba(18, 20, 26, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.8), 0 0 40px rgba(255, 255, 255, 0.03);
        }

        .promo-left {
          flex: 1.4;
          padding: 2.25rem 2rem;
          text-align: left;
        }

        .promo-right {
          flex: 1;
          position: relative;
          background: rgba(255, 255, 255, 0.02);
          border-left: 1px solid rgba(255, 255, 255, 0.08);
          padding: 2.25rem 1.75rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.6rem;
        }

        .promo-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.07);
          color: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.15);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          padding: 6px 12px;
          border-radius: 999px;
          margin-bottom: 1rem;
        }
        .promo-badge-icon { font-size: 0.8rem; opacity: 0.7; }

        .promo-heading {
          color: #fff;
          font-size: 1.9rem;
          line-height: 1.15;
          font-weight: 800;
          margin: 0 0 0.6rem;
        }
        .promo-accent {
          background: linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .promo-keywords {
          color: #94a3b8;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          margin: 0 0 0.9rem;
        }

        .promo-desc {
          color: rgba(255,255,255,0.7);
          font-size: 0.92rem;
          line-height: 1.5;
          margin: 0 0 1rem;
        }

        .promo-note {
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.9);
          font-size: 0.85rem;
          font-weight: 600;
        }
        .promo-check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px; height: 18px;
          border: 1.5px solid rgba(255, 255, 255, 0.4);
          color: #fff;
          border-radius: 50%;
          font-size: 0.65rem;
        }

        .promo-divider {
          height: 1px;
          background: rgba(255,255,255,0.1);
          margin: 1.1rem 0 0.9rem;
        }

        .promo-footer-text {
          color: rgba(255,255,255,0.5);
          font-size: 0.82rem;
          margin: 0;
        }

        .promo-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 32px; height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(255,255,255,0.08);
          color: #fff;
          font-size: 0.9rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }
        .promo-close:hover { background: rgba(255,255,255,0.2); }

        .promo-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 1.5rem;
        }

        .promo-app-title { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0.4rem 0 0; }
        .promo-app-sub { color: rgba(255,255,255,0.45); font-size: 0.78rem; margin: 0 0 0.9rem; }

        .promo-store-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 0.82rem;
          position: relative;
          transition: all 0.2s ease;
        }
        .promo-store-btn span { display: flex; flex-direction: column; text-align: left; }
        .promo-store-btn em { font-style: normal; font-size: 0.62rem; opacity: 0.8; }

        .promo-store-btn--primary {
          background: #ffffff;
          color: #0f172a;
          font-weight: 700;
          box-shadow: 0 4px 16px rgba(255, 255, 255, 0.15);
        }
        .promo-store-btn--primary:hover {
          background: #f1f5f9;
          transform: translateY(-1px);
          box-shadow: 0 6px 22px rgba(255, 255, 255, 0.25);
        }
        .promo-store-btn--disabled {
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.4);
          cursor: not-allowed;
        }
        .promo-soon-tag {
          margin-left: auto;
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.15);
          font-size: 0.6rem;
          font-weight: 700;
          padding: 3px 7px;
          border-radius: 999px;
          align-self: center;
        }

        @media (max-width: 640px) {
          .promo-overlay {
            padding: 0.75rem;
            align-items: center;
          }
          .promo-card {
            flex-direction: column;
            max-height: 85vh;
            border-radius: 16px;
            overflow-y: auto;
          }
          .promo-left {
            padding: 1.25rem 1rem 0.75rem;
          }
          .promo-badge {
            font-size: 0.62rem;
            padding: 4px 9px;
            margin-bottom: 0.6rem;
          }
          .promo-heading {
            font-size: 1.3rem;
            margin-bottom: 0.3rem;
          }
          .promo-keywords {
            font-size: 0.6rem;
            margin-bottom: 0.5rem;
            letter-spacing: 0.02em;
            word-break: break-word;
          }
          .promo-desc {
            font-size: 0.78rem;
            line-height: 1.4;
            margin-bottom: 0.6rem;
          }
          .promo-note {
            font-size: 0.74rem;
          }
          .promo-check {
            width: 15px; height: 15px;
            font-size: 0.55rem;
          }
          .promo-divider {
            margin: 0.6rem 0 0.4rem;
          }
          .promo-footer-text {
            font-size: 0.74rem;
          }
          .promo-right {
            border-left: none;
            border-top: 1px solid rgba(255,255,255,0.08);
            padding: 0.9rem 1rem 1.1rem;
            gap: 0.4rem;
          }
          .promo-icon {
            display: none;
          }
          .promo-app-title {
            font-size: 0.9rem;
            margin-top: 0;
          }
          .promo-app-sub {
            font-size: 0.7rem;
            margin-bottom: 0.5rem;
          }
          .promo-store-btn {
            padding: 8px 12px;
            font-size: 0.75rem;
          }
          .promo-store-btn em {
            font-size: 0.58rem;
          }
          .promo-close {
            top: 8px;
            right: 8px;
            width: 26px;
            height: 26px;
            font-size: 0.75rem;
          }
        }

        @keyframes wordFade {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-word { animation: wordFade 0.6s ease forwards; }

        @keyframes shimmerSweep {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        .shimmer-sweep { animation: shimmerSweep 3s infinite linear; }
      `}</style>



      <section className={styles.hero} onMouseMove={onHeroMove}>
        <ParticleCanvas />
        <div className={styles.heroMesh} ref={meshRef} aria-hidden="true" />

        <span className={styles.heroVertLabel} aria-hidden="true">
          Digital Studio — Est. 2024
        </span>

        <div className={styles.heroInner}>
          <p className={styles.heroEyebrow}>
            End-To-End Digital Solutions Designed for Ambitious businesses.
            Scalable and Experience-Driven Industry-leading Work
          </p>

          <h1 className={styles.heroTitle}>
            <span className={styles.heroLineWrap}>
              <SplitWords text="Building" charClass={styles.char} />
            </span>
            <span className={`${styles.heroLineWrap} ${styles.heroAccentLine}`}>
              <SplitWords text="Digital" charClass={styles.charAccent} />
              {' '}
              <SplitWords text="Solutions" charClass={styles.charOutline} />
            </span>
            <span className={`${styles.heroLineWrap} ${styles.desktopInline}`}>
              <SplitWords text="That" charClass={styles.char} />
            </span>
            {' '}
            <span className={`${styles.heroLineWrap} ${styles.desktopInline}`}>
              <SplitWords text="Matter" charClass={styles.char} />
            </span>
          </h1>

          <div className={styles.heroCtas}>
            <Magnetic>
              <LiquidBtn to="/services" variant="red" size="lg">Get Started</LiquidBtn>
            </Magnetic>
            <Magnetic>
              <LiquidBtn to="/projects" variant="ghost" size="lg">View Projects</LiquidBtn>
            </Magnetic>
          </div>

          <div className={styles.statsRow}>
            {stats.map(s => (
              <div key={s.label} className={styles.stat}>
                <strong>
                  <Counter target={parseInt(s.value)} suffix={s.suffix || (s.value.includes('+') ? '+' : '')} />
                </strong>
                <span>{s.label}</span>
              </div>
            ))}
            <div className={styles.statDivider} />
            <div className={`${styles.stat} ${styles.statAvail}`}>
              <span className={styles.availDot} />
              <span>Currently accepting new projects</span>
            </div>
          </div>
        </div>

        <div className={styles.scrollHint} aria-hidden="true">
          <span className={styles.scrollLine} />
          <span className={styles.scrollLabel}>Scroll</span>
        </div>
      </section>

      <Ticker items={['Web Development', 'Mobile Apps', 'UI Design', 'Brand Experiences', 'Scalable Systems']} speed={35} />

      {/* ════════════════════════════════════════ TRUSTED BY MARQUEE ════════════════════════════════════════ */}
      <div className={styles.trustedSection}>
        <p className={styles.trustedLabel}>Trusted by Innovative Businesses Worldwide</p>
        <div className={styles.marqueeWrapper}>
          <div className={styles.marqueeTrack}>
            {[...trustedBy, ...trustedBy, ...trustedBy].map((t, i) => (
              <div key={i} className={styles.trustedItem}>
                <img src={t.img} alt={t.name} className={styles.logoImg} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className={styles.services}>
        <div className={styles.servicesBg} aria-hidden="true" />

        <div className={styles.servicesInner}>
          <div className={styles.servicesHead} data-reveal>
            <span className={styles.sectionKicker}>What we build</span>
            <h2 className={styles.sectionTitle}>
              Crafted with{' '}
              <em className={styles.titleEm}>precision</em>
            </h2>
          </div>

          {services.map((svc, i) => (
            <article
              key={svc.title}
              className={`${styles.svcPanel} ${i % 2 !== 0 ? styles.svcPanelFlip : ''}`}
            >
              <Link to="/contact-us" className={styles.svcVisualWrap} aria-label="Contact us" data-cursor>
                <div className={styles.imgTileGrid}>
                  {[0, 1, 2, 3].map(j => (
                    <div key={j} className={styles.imgTile}>
                      <img
                        src={svc.visual}
                        alt={j === 0 ? svc.title : ''}
                        className={styles.imgTileImg}
                      />
                    </div>
                  ))}
                </div>
                <div className={styles.imgGlow} aria-hidden="true" />
                <div className={styles.imgBadge} aria-hidden="true">
                  <span>View Work</span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 12L12 2M12 2H5M12 2V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
              </Link>

              <div className={styles.svcText}>
                <span className={styles.svcTag} data-cursor>{svc.tag}</span>
                <h2 className={styles.svcH2}>
                  <SplitWords text={svc.title} charClass={styles.char} />
                </h2>
                <p className={styles.svcBody}>{svc.body}</p>
                <Magnetic>
                  <LiquidBtn to={svc.to} variant="red">{svc.cta}</LiquidBtn>
                </Magnetic>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Ticker items={['Innovative Design', 'Precision Engineering', 'Scalable Architecture', 'Bold Vision', 'Clean Code']} speed={28} reverse />


      <section className={styles.blurbs}>
        <div className={styles.blurbsWave} aria-hidden="true">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,0 C480,100 960,0 1440,80 L1440,100 L0,100 Z" />
          </svg>
        </div>

        <div className={styles.blurbsInner}>
          <div className={styles.servicesHead} data-reveal>
            <span className={styles.sectionKicker} style={{ color: 'rgba(255,255,255,0.4)' }}>Our edge</span>
            <h2 className={styles.sectionTitle} style={{ color: '#fff' }}>
              Why teams{' '}
              <em className={styles.titleEmLight}>choose us</em>
            </h2>
          </div>

          <div className={styles.blurbGrid}>
            {footerBlurbs.map((b, i) => (
              <div key={b.heading} className={styles.blurbCard} data-reveal-card>
                <div className={styles.blurbCardInner}>
                  <div className={styles.blurbBorderAnim} aria-hidden="true" />
                  <div className={styles.blurbNum}>{String(i + 1).padStart(2, '0')}</div>
                  <h4 className={styles.blurbH4}>{b.heading}</h4>
                  <p className={styles.blurbP}>{b.body}</p>
                  <div className={styles.blurbFoot}>
                    <Magnetic strength={0.2}>
                      <button className={styles.blurbCta} data-cursor>Learn more ↗</button>
                    </Magnetic>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}